/**
 * students.js — React Query hooks for Admin Student Management
 *
 * Hooks:
 *   useStudentList(filters)      — paginated + filtered student list
 *   useStudent(userId)           — full detail (user + enrollments + certs + activity + payments)
 *   useStudentLedger(userId)     — Muhasebe FAZ 3.6: borç/tahsilat read-model
 *                                  (GET /admin/students/{id}/accounting/ledger)
 *   useUpdateStudent(userId)     — profile mutation (allowlist)
 *   useDeleteStudent(userId)     — KVKK soft-delete trigger
 *   useCancelDeletion(userId)    — admin override (is_deletion_pending: false)
 *   useEnrollmentList(filters)   — all enrollments for AdminEnrollments page
 *   useAdminEnrollDelete()       — delete a single enrollment
 *   useAdminEnrollComplete()     — manually complete enrollment
 *   useAddManualPayment(userId)  — Muhasebe FAZ 3.4: admin manuel tahsilat/peşinat
 *                                  (POST /admin/students/{id}/add-payment)
 *   useIssueCertificate(userId)  — admin elle sertifika üretir/getirir
 *                                  (POST /admin/certificates/issue)
 *   useStudentPendingPayments(userId) — banka eşleşme havuzu "Elle Seç" akışı
 *                                  (GET /admin/students/{id}/pending-payments,
 *                                  bkz. hook docstring'i).
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';

// ── Query keys ────────────────────────────────────────────────────────────────

export const studentKeys = {
  all: ['admin', 'students'],
  list: (filters) => [...studentKeys.all, 'list', filters],
  detail: (userId) => [...studentKeys.all, 'detail', userId],
  enrollments: (filters) => ['admin', 'enrollments', 'list', filters],
  ledger: (userId) => [...studentKeys.all, 'ledger', userId],
  pendingPayments: (userId) => [...studentKeys.all, 'pendingPayments', userId],
};

// ── useStudentList ─────────────────────────────────────────────────────────────

/**
 * Paginated, filtered student list.
 *
 * @param {Object} filters
 * @param {string}  filters.search
 * @param {string}  filters.role
 * @param {string}  filters.cabin_crew_level
 * @param {boolean} filters.is_deletion_pending
 * @param {number}  filters.page
 * @param {number}  filters.limit
 */
export function useStudentList(filters = {}) {
  const { authHeaders } = useAuth();
  const { search = '', role = '', cabin_crew_level = '', is_deletion_pending = false, page = 0, limit = 20 } = filters;

  return useQuery({
    queryKey: studentKeys.list({ search, role, cabin_crew_level, is_deletion_pending, page, limit }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (role) params.set('role', role);
      if (cabin_crew_level) params.set('cabin_crew_level', cabin_crew_level);
      if (is_deletion_pending) params.set('is_deletion_pending', 'true');
      params.set('skip', String(page * limit));
      params.set('limit', String(limit));

      const res = await apiClient.get(`/admin/users?${params}`, { headers: authHeaders() });
      return res.data; // { users: [], total: number }
    },
    placeholderData: keepPreviousData,
  });
}

// ── useStudent (full detail) ──────────────────────────────────────────────────

/**
 * Fetches full student detail: profile + enrollments + certs + activity + payments.
 * Aggregates from multiple endpoints.
 *
 * @param {string|null} userId
 */
export function useStudent(userId) {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: studentKeys.detail(userId),
    queryFn: async () => {
      const headers = authHeaders();

      // Core detail (includes enrollments + certificates)
      const detailRes = await apiClient.get(`/admin/students/${userId}/detail`, { headers });
      const detail = detailRes.data;

      // Documents
      let documents = [];
      try {
        const docsRes = await apiClient.get(`/admin/users/${userId}/documents`, { headers });
        documents = docsRes.data.documents || [];
      } catch { /* non-critical */ }

      // Payments & activity — detail response zaten iceriyor (ayri endpoint yok)
      const payments = detail.payments || [];
      const activity = detail.activity_logs || [];

      // Backend /admin/students/{id}/detail returns the user document under
      // `student`. The UI expects `user` (legacy contract). Normalise here so a
      // single source of truth exists for the form/header components.
      const user = detail.user || detail.student || {};

      return {
        ...detail,
        user,
        documents,
        payments,
        activity,
      };
    },
    enabled: !!userId,
    staleTime: 10_000,
  });
}

// ── useStudentLedger (Muhasebe FAZ 3.6 — DebtLedgerTable) ────────────────────

/**
 * Tek öğrencinin borç/tahsilat read-model'i (Muhasebe FAZ 3.2/3.6, PLAN §11).
 * `useStudent` içindeki `payments`/`installments` (özet sekmesi) ile
 * KARIŞTIRILMASIN — bu ayrı bir backend endpoint'i, invoice+installment
 * kırılımını ve zenginleştirilmiş (banka/senet/kaydeden) tahsilat satırlarını
 * ayrı iki liste + canonical `summary` olarak döner.
 *
 * @param {string|null} userId
 * @returns {{ user_id, debt_rows: [], collection_rows: [], summary: {} }}
 */
export function useStudentLedger(userId) {
  const { authHeaders } = useAuth();

  return useQuery({
    queryKey: studentKeys.ledger(userId),
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/students/${userId}/accounting/ledger`,
        { headers: authHeaders() }
      );
      return res.data;
    },
    enabled: !!userId,
    staleTime: 10_000,
  });
}

// ── useStudentPendingPayments (Banka eşleşme havuzu "Elle Seç" akışı) ───────────

/**
 * `GET /admin/students/{user_id}/pending-payments` — backend HAZIR (Tur 3'te
 * routes/admin.py + services/bank_transaction_service.py::
 * list_pending_payments_for_student salt okunarak doğrulandı; route
 * dosyasındaki eski docstring `match_type`/`installment_id`'den bahsetmiyordu
 * — SERVİS fonksiyonunun docstring'i güncel/kanonik, kontratla birebir
 * eşleşiyor).
 *
 * Öğrencinin açık taksitlerinin `payments` koleksiyonunda yer tutucusu
 * OLMADIĞI için liste hem (eski akış) bekleyen ödemeleri HEM (modern akış)
 * doğrudan açık taksitleri birlikte döner:
 * `{ items: [{match_type: "payment"|"installment", payment_id: string|null,
 * installment_id: string|null, amount, payment_ref, course_title, due_date,
 * installment_no}] }` — `match_type="payment"` iken `payment_id` dolu/
 * `installment_id` null, `match_type="installment"` iken tersi. Kart tek
 * çekim (`method=credit_card`) taksitler LİSTEDE HİÇ GÖRÜNMEZ (backend
 * filtreliyor — banka havalesiyle eşleşemezler).
 *
 * Kullanım yeri: `BankMatchingCard.jsx` "Elle Seç" paneli — öğrenci
 * seçildiğinde bu öğrencinin açık/bekleyen kayıtlarını listeler, admin
 * `match_type`'a göre `payment_id` VEYA `installment_id` ile eşleştirir
 * (bkz. `useMatchBankTransaction`). `retry:false` savunma amaçlı korunur
 * (gerçek ağ hatasında panel boş/hata durumunu gösterir, sayfa KIRILMAZ).
 */
export function useStudentPendingPayments(userId) {
  const { authHeaders } = useAuth();
  return useQuery({
    queryKey: studentKeys.pendingPayments(userId),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/students/${userId}/pending-payments`, { headers: authHeaders() });
      return res.data;
    },
    enabled: !!userId,
    retry: false,
  });
}

// ── useUpdateStudent ──────────────────────────────────────────────────────────

/**
 * Updates student profile fields (backend allowlist applies).
 *
 * IMPORTANT: callers must NOT include "role" in the fields object.
 * Role changes must go through PUT /admin/users/{id}/role (dedicated endpoint).
 * Including "role" here would be silently ignored by the backend allowlist today,
 * but could become a privilege-escalation vector if the allowlist is extended.
 *
 * IMPORTANT: use "tc_no" (not "tc_kimlik") to match the backend allowlist.
 */
export function useUpdateStudent(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (fields) => {
      // Guard: strip role and tc_kimlik defensively so callers can't accidentally
      // send them even if they pass a full user document.
       
      const { role, tc_kimlik, ...safeFields } = fields;
      return apiClient.put(`/admin/students/${userId}/profile`, safeFields, { headers: authHeaders() });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

// ── useDeleteStudent ──────────────────────────────────────────────────────────

/**
 * Hard-deletes student account (KVKK purge). Destructive.
 */
export function useDeleteStudent() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (userId) =>
      apiClient.delete(`/admin/users/${userId}`, { headers: authHeaders() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

// ── useCancelDeletion ─────────────────────────────────────────────────────────

/**
 * Admin override: cancel KVKK soft-delete for a user.
 * Sets is_deletion_pending=false, clears deletion_scheduled_at & deletion_grace_until.
 */
export function useCancelDeletion() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (userId) =>
      apiClient.put(
        `/admin/students/${userId}/profile`,
        {
          is_deletion_pending: false,
          deletion_scheduled_at: null,
          deletion_grace_until: null,
        },
        { headers: authHeaders() }
      ),
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

// ── useEnrollmentList ─────────────────────────────────────────────────────────

/**
 * Fetches admin-level enrollment list.
 * If GET /admin/enrollments exists, uses it.
 * Fallback: GET /admin/courses with enrolled_count as aggregate.
 *
 * @param {Object} filters
 * @param {string} filters.search
 * @param {string} filters.course_id
 * @param {string} filters.status      active | completed | cancelled
 * @param {number} filters.page
 * @param {number} filters.limit
 */
export function useEnrollmentList(filters = {}) {
  const { authHeaders } = useAuth();
  const { search = '', course_id = '', status = '', page = 0, limit = 30 } = filters;

  return useQuery({
    queryKey: studentKeys.enrollments({ search, course_id, status, page, limit }),
    queryFn: async () => {
      const headers = authHeaders();

      // Try dedicated endpoint first
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (course_id) params.set('course_id', course_id);
        if (status) params.set('status', status);
        params.set('skip', String(page * limit));
        params.set('limit', String(limit));

        const res = await apiClient.get(`/admin/enrollments?${params}`, { headers });
        return res.data; // { enrollments: [], total: number }
      } catch {
        // Fallback: aggregate from courses
        const coursesRes = await apiClient.get('/admin/courses', { headers });
        const courses = coursesRes.data.courses || [];
        const enrollments = courses.map((c) => ({
          course_id: c.course_id,
          course_title: c.title,
          enrolled_count: c.enrolled_count || 0,
          category: c.category,
          price: c.price,
          currency: c.currency,
          status: 'aggregate',
        }));
        return { enrollments, total: enrollments.length, _fallback: true };
      }
    },
    placeholderData: keepPreviousData,
  });
}

// ── useAdminEnrollDelete ──────────────────────────────────────────────────────

export function useAdminEnrollDelete() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, courseId }) =>
      apiClient.delete(`/admin/students/${userId}/enrollments/${courseId}`, { headers: authHeaders() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
      qc.invalidateQueries({ queryKey: studentKeys.all });
    },
  });
}

// ── useAdminEnrollComplete ────────────────────────────────────────────────────

export function useAdminEnrollComplete() {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, courseId }) =>
      apiClient.put(
        `/admin/students/${userId}/enrollments/${courseId}/complete`,
        {},
        { headers: authHeaders() }
      ),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
    },
  });
}

// ── useAdminEnrollUser ────────────────────────────────────────────────────────

/**
 * Enroll an existing user into one or more courses.
 * POST /admin/students/{user_id}/enroll-courses
 *
 * @param {string} userId
 * Payload: { courses: [{ course_id, price_override }], payment_method, ... }
 * Minimal use: { courses: [{ course_id }] }
 */
export function useAdminEnrollUser(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) =>
      apiClient.post(
        `/admin/students/${userId}/enroll-courses`,
        payload,
        { headers: authHeaders() }
      ).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
      qc.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
    },
  });
}

// ── useAddManualPayment (Muhasebe FAZ 3.4 — CollectionForm) ──────────────────

/**
 * Admin manuel tahsilat/peşinat ekler. POST /admin/students/{id}/add-payment
 *
 * Payload: { amount, method="cash", description="", course_id="",
 *            bank_id (ZORUNLU), kind: "manual"|"down_payment"="manual",
 *            vat_override_amount=null, muhasebe_tipi=null }
 *
 * Başarıda hem ledger (borç/tahsilat listesi + `summary` bakiyesi) hem de
 * detail (özet kartları + sekme sayacı) invalidate edilir — ikisi de bu
 * mutasyondan etkilenir (bkz. görev talimatı).
 *
 * @param {string} userId
 */
export function useAddManualPayment(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) =>
      apiClient
        .post(`/admin/students/${userId}/add-payment`, payload, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.ledger(userId) });
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
    },
  });
}

// ── useIssueCertificate (admin manual "generate certificate" action) ────────

/**
 * Admin, bir öğrenci + kurs için sertifika üretir/getirir.
 * POST /admin/certificates/issue — body: { user_id, course_id }
 *
 * İdempotent: sertifika zaten varsa backend aynı kaydı döner (yeni üretmez).
 * Guard hataları (400 "Not enrolled"/"Course not completed", 403
 * exam_not_passed) getApiErrorMessage ile mesaja çevrilip caller'da toast'a
 * yansıtılır.
 *
 * @param {string} userId
 */
export function useIssueCertificate(userId) {
  const { authHeaders } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (courseId) =>
      apiClient
        .post('/admin/certificates/issue', { user_id: userId, course_id: courseId }, { headers: authHeaders() })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentKeys.detail(userId) });
    },
  });
}
